import React, {
  useState, useCallback, useEffect, useRef, useMemo,
} from 'react'
import { connect } from 'react-redux'
import { SearchContext } from '../../services/search/SearchContext'
import { SupplementarySearchContext } from '../../services/search/SupplementarySearchContext'
import { reportLocalVsAlgolia } from '../../services/search/searchReporter'
import AlgoliaSearchProvider from '../../services/search/providers/AlgoliaSearchProvider'
import ShadowProvider from '../../services/search/providers/ShadowProvider'
import HelpSearchProvider from '../../services/search/providers/HelpSearchProvider'
import LocalContentSearchProvider from '../../services/search/providers/LocalContentSearchProvider'
import MiniSearchContentProvider from '../../services/search/providers/MiniSearchContentProvider'
import NavigationSearchProvider from '../../services/search/providers/NavigationSearchProvider'
import ExternalLinksSearchProvider from '../../services/search/providers/ExternalLinksSearchProvider'
import QuickActionsSearchProvider from '../../services/search/providers/QuickActionsSearchProvider'

// ─── Provider role config ──────────────────────────────────────────────────────
// Extend this list to add/remove providers without changing any consumer code.
// role: 'display'  → results populate SearchContext (shown to the user)
// role: 'shadow'   → results sent to searchReporter only (quality assessment)
// role: 'additive' → results populate SupplementarySearchContext (supplementary UI slots)

// Feature flags are read from environment variables here. Wire to a Redux
// flag slice or a remote config service later if runtime toggling is needed.
function readSearchFeatureFlags() {
  return {
    localSearch: process.env.REACT_APP_LOCAL_SEARCH_ENABLED === 'true',
    miniSearch: process.env.REACT_APP_MINISEARCH_ENABLED === 'true',
    // shadowSearch: false,
    // helpSearch: false,
  }
}

function buildProviderConfig(algoliaSearchKey, featureFlags) {
  const config = []
  if (algoliaSearchKey) {
    config.push({
      id: 'algolia', role: 'display', component: AlgoliaSearchProvider,
    })
  }
  // Quick Actions — always on. These are static UI affordances (New Set,
  // New Pack, New Class, New Question, …) defined in quickActionsRegistry.
  // No user data, no network calls — safe to ship unflagged.
  config.push({
    id: 'quick-actions', role: 'additive', label: 'Quick Actions', component: QuickActionsSearchProvider,
  })
  // Shadow/challenger provider — enable via featureFlags.shadowSearch
  if (algoliaSearchKey && featureFlags.shadowSearch) {
    config.push({
      id: 'shadow', role: 'shadow', component: ShadowProvider,
    })
  }
  // Additive help-article provider — enable via featureFlags.helpSearch
  if (featureFlags.helpSearch) {
    config.push({
      id: 'help', role: 'additive', label: 'Help Articles', component: HelpSearchProvider,
    })
  }
  // Additive local-search providers — enable via featureFlags.localSearch
  // (REACT_APP_LOCAL_SEARCH_ENABLED). Each lands in SupplementarySearchContext
  // under its own providerId so consumers can read them independently.
  if (featureFlags.localSearch) {
    config.push({
      id: 'local-content', role: 'additive', label: 'Library (Local)', component: LocalContentSearchProvider,
    })
    config.push({
      id: 'navigation', role: 'additive', label: 'Navigation', component: NavigationSearchProvider,
    })
    config.push({
      id: 'external-help', role: 'additive', label: 'Help & External', component: ExternalLinksSearchProvider,
    })
  }
  // Secondary BM25 local-search provider — enable via featureFlags.miniSearch
  // (REACT_APP_MINISEARCH_ENABLED). Renders alongside the Fuse-based
  // local-content provider so the two engines can be compared visually
  // and through searchReporter against Algolia.
  if (featureFlags.miniSearch) {
    config.push({
      id: 'minisearch-content', role: 'additive', label: 'Library (BM25)', component: MiniSearchContentProvider,
    })
  }
  return config
}

// ─── Options builder ───────────────────────────────────────────────────────────

const TOP_NAV_HITS_PER_PAGE = 4
const SECTION_HOME_HITS_PER_PAGE = 200
const CONTENT_SEARCH_FILTERS = 'archived!=1 AND parentArchived!=1'

function buildAlgoliaOptions(isTopNav, isSectionHome, hitsPerPage, algoliaSearchKey) {
  const primaryIndex = isSectionHome ? 'sets_questions' : 'repos'
  // Secondary indexes are queried in parallel for multi-index search.
  // isSectionHome queries only the primary index; all other contexts include
  // both folders and sets_questions alongside repos.
  const secondaryIndexes = isSectionHome ? [] : ['folders', 'sets_questions']
  let resolvedHitsPerPage = hitsPerPage != null ? hitsPerPage : null
  if (isTopNav) resolvedHitsPerPage = TOP_NAV_HITS_PER_PAGE
  else if (isSectionHome) resolvedHitsPerPage = SECTION_HOME_HITS_PER_PAGE
  return {
    apiKey: algoliaSearchKey,
    primaryIndex,
    secondaryIndexes,
    hitsPerPage: resolvedHitsPerPage,
    filters: CONTENT_SEARCH_FILTERS,
  }
}

// ─── Default context value ─────────────────────────────────────────────────────

const DEFAULT_INDEX_RESULT = {
  hits: [], nbHits: 0, hasMore: false, loadMore: () => {},
}

const DEFAULT_RESULTS = {
  suggestions: [],
  hits: [],
  indexResults: {
    repos: { ...DEFAULT_INDEX_RESULT },
    folders: { ...DEFAULT_INDEX_RESULT },
    // eslint-disable-next-line camelcase
    sets_questions: { ...DEFAULT_INDEX_RESULT },
  },
}

// ─── SearchOrchestrator ────────────────────────────────────────────────────────

/**
 * Owns query state and renders all search providers as headless siblings.
 * Routes each provider's results to the correct context based on its role.
 *
 * Consumer components (TopNavSearchBar, SearchResultsContainer, etc.) read only
 * from SearchContext or SupplementarySearchContext — they have no knowledge of
 * which providers are active.
 */
function SearchOrchestrator({
  algoliaSearchKey,
  isTopNav,
  isSectionHome,
  hitsPerPage,
  searchState,
  onSearchStateChange,
  children,
}) {
  const [query, setQuery] = useState(() => (searchState && searchState.query) || '')
  const [displayResults, setDisplayResults] = useState(DEFAULT_RESULTS)
  const [additiveResults, setAdditiveResults] = useState([])

  // Sync query when the URL-driven searchState prop changes (e.g. browser back).
  // React bails out of a re-render when setQuery is called with the same value,
  // so there is no risk of an infinite loop with the onSearchStateChange effect.
  useEffect(() => {
    const externalQuery = (searchState && searchState.query) || ''
    setQuery(externalQuery)
  }, [searchState])

  // Notify the parent (e.g. SearchResultsContainer) when the query changes so
  // it can debounce URL updates. Use a ref so onSearchStateChange can change
  // without this effect becoming stale.
  const onSearchStateChangeRef = useRef(onSearchStateChange)
  onSearchStateChangeRef.current = onSearchStateChange
  useEffect(() => {
    if (onSearchStateChangeRef.current) {
      onSearchStateChangeRef.current({ query })
    }
  }, [query])

  // Route results based on the provider's role.
  //
  // Additive blocks carry both the flat `hits` list and the optional
  // `indexResults` object. Providers that mirror Algolia's per-index shape
  // (currently LocalContentSearchProvider) populate `indexResults`; lightweight
  // additive providers (navigation, external-help) leave it undefined and
  // consumers fall back to reading flat `hits`.
  const handleResults = useCallback((role, providerId, results) => {
    if (role === 'display') {
      setDisplayResults(results)
    } else if (role === 'additive') {
      setAdditiveResults((prev) => {
        const without = prev.filter((r) => r.providerId !== providerId)
        return [...without, {
          providerId,
          hits: results.hits || [],
          indexResults: results.indexResults,
        }]
      })
    }
    // 'shadow' role is handled inside ShadowProvider via searchReporter directly.
  }, [])

  // Stable references — providers receive the same options object identity
  // until their inputs actually change, preventing cascading re-renders that
  // would re-trigger Algolia's connector HOCs.
  const algoliaOptions = useMemo(
    () => buildAlgoliaOptions(isTopNav, isSectionHome, hitsPerPage, algoliaSearchKey),
    [isTopNav, isSectionHome, hitsPerPage, algoliaSearchKey],
  )
  // Feature flags are baked at build time by CRA (env vars are statically
  // replaced), so re-reading them on dependency change is effectively a no-op
  // beyond the initial build. Keeping algoliaSearchKey as the only dep is
  // sufficient.
  const providerConfig = useMemo(
    () => buildProviderConfig(algoliaSearchKey, readSearchFeatureFlags()),
    [algoliaSearchKey],
  )
  const displayProviderId = useMemo(
    () => (providerConfig.find((p) => p.role === 'display') || {}).id || 'unknown',
    [providerConfig],
  )

  // Memoised per-provider onResults callback. Identity is stable for the
  // lifetime of the orchestrator since handleResults is stable.
  const onResultsForProvider = useCallback(
    (role, id) => (results) => handleResults(role, id, results),
    [handleResults],
  )
  const onResultsCacheRef = useRef(new Map())
  const getOnResults = (role, id) => {
    const key = `${role}:${id}`
    if (!onResultsCacheRef.current.has(key)) {
      onResultsCacheRef.current.set(key, onResultsForProvider(role, id))
    }
    return onResultsCacheRef.current.get(key)
  }

  const searchContextValue = useMemo(
    () => ({
      ...displayResults,
      currentRefinement: query,
      refine: setQuery,
    }),
    [displayResults, query],
  )

  const supplementaryContextValue = useMemo(
    () => ({ additiveResults }),
    [additiveResults],
  )

  // Option B — side-by-side Algolia vs. local diagnostic. Fires on every
  // settled result update for the current query while the local-search flag
  // is on. Cost is one console.groupCollapsed + a few console.tables.
  // Gated on REACT_APP_LOCAL_SEARCH_ENABLED so production never pays for it.
  const localSearchEnabled = process.env.REACT_APP_LOCAL_SEARCH_ENABLED === 'true'
  useEffect(() => {
    if (!localSearchEnabled) return
    if (!query) return
    reportLocalVsAlgolia({
      query,
      displayedProvider: displayProviderId,
      displayedResults: displayResults,
      additiveResults,
    })
  }, [
    localSearchEnabled,
    query,
    displayProviderId,
    displayResults,
    additiveResults,
  ])

  if (algoliaSearchKey) {
    return (
      <SearchContext.Provider value={searchContextValue}>
        <SupplementarySearchContext.Provider value={supplementaryContextValue}>
          {providerConfig.map(({ id, role, component: Provider }) => (
            <Provider
              key={id}
              query={query}
              options={algoliaOptions}
              onResults={getOnResults(role, id)}
              displayedResults={displayResults}
              displayedProvider={displayProviderId}
            />
          ))}
          {children}
        </SupplementarySearchContext.Provider>
      </SearchContext.Provider>
    )
  }
  if (isTopNav) {
    return <input disabled className='topNav-search' />
  }
  return null
}

function mapStateToProps(state) {
  const { searchKey } = state
  let contentsSearchKey
  if (searchKey) {
    contentsSearchKey = searchKey.contents
  }
  return {
    algoliaSearchKey: contentsSearchKey,
  }
}

export default connect(
  mapStateToProps,
  {},
)(SearchOrchestrator)
